import React from 'react';
import { AlertCircle, AlertTriangle, RefreshCw, Eye, Upload } from 'lucide-react';
import { RequestStatusDetails } from './request-table/request-status-details';
import { RequestsTableResponse, requestStatus } from 'src/lib/services/api/request-api';

export interface RequestStatusConfig {
  bgColor: string;
  badgeColor: string;
  buttonColor: string;
  buttonText: string;
  icon: React.ReactNode;
  urgency: 'high' | 'medium' | 'low';
}

/**
 * Get the status configuration for a request based on field-based detection
 * This is the unified logic used across dashboard and requests list
 */
export const getRequestStatusConfig = (request: any, hasUnread?: boolean): RequestStatusConfig => {
  // Check for needs re-edit first (highest priority)
  if (request.requested_reedit_at) {
    return {
      bgColor: 'tw-bg-red-50 tw-border-red-200',
      badgeColor: 'tw-bg-red-100 tw-text-red-700 tw-border-red-300',
      buttonColor: 'tw-bg-red-600 hover:tw-bg-red-700 tw-text-white',
      buttonText: 'Provide Feedback',
      icon: <AlertCircle className="tw-h-4 tw-w-4 tw-text-red-500" />,
      urgency: 'high',
    };
  }

  // Check for unread messages (same treatment as needs attention)
  if (hasUnread) {
    return {
      bgColor: 'tw-bg-red-50 tw-border-red-200',
      badgeColor: 'tw-bg-red-100 tw-text-red-700 tw-border-red-300',
      buttonColor: 'tw-bg-red-600 hover:tw-bg-red-700 tw-text-white',
      buttonText: 'Review Now',
      icon: <AlertCircle className="tw-w-4 tw-h-4 tw-text-red-500" />,
      urgency: 'high',
    };
  }

  // Check for needs attention
  if (request.requested_attention_at) {
    return {
      bgColor: 'tw-bg-red-50 tw-border-red-200',
      badgeColor: 'tw-bg-red-100 tw-text-red-700 tw-border-red-300',
      buttonColor: 'tw-bg-red-600 hover:tw-bg-red-700 tw-text-white',
      buttonText: 'Review Now',
      icon: <AlertCircle className="tw-h-4 tw-w-4 tw-text-red-500" />,
      urgency: 'high',
    };
  }

  // Fall back to status-based logic for other cases
  switch (request.status) {
    case requestStatus.delivered:
      return {
        bgColor: 'tw-bg-red-50 tw-border-red-200',
        badgeColor: 'tw-bg-red-100 tw-text-red-700 tw-border-red-300',
        buttonColor: 'tw-bg-red-600 hover:tw-bg-red-700 tw-text-white',
        buttonText: 'Review Now',
        icon: <AlertCircle className="tw-h-4 tw-w-4 tw-text-red-500" />,
        urgency: 'high',
      };
    case requestStatus.uploading:
      return {
        bgColor: 'tw-bg-amber-50 tw-border-amber-200',
        badgeColor: 'tw-bg-amber-100 tw-text-amber-700 tw-border-amber-300',
        buttonColor: 'tw-bg-amber-600 hover:tw-bg-amber-700 tw-text-white',
        buttonText: 'Uploading...',
        icon: <Upload className="tw-w-4 tw-h-4 tw-text-amber-500" />,
        urgency: 'medium',
      };
    case requestStatus.queued:
      return {
        bgColor: 'tw-bg-yellow-50 tw-border-yellow-200',
        badgeColor: 'tw-bg-yellow-100 tw-text-yellow-700 tw-border-yellow-300',
        buttonColor: 'tw-bg-yellow-600 hover:tw-bg-yellow-700 tw-text-white',
        buttonText: 'View Details',
        icon: <AlertTriangle className="tw-w-4 tw-h-4 tw-text-yellow-500" />,
        urgency: 'medium',
      };
    case requestStatus.currentlyEditing:
    case 'editing':
      return {
        bgColor: 'tw-bg-blue-50 tw-border-blue-200',
        badgeColor: 'tw-bg-blue-100 tw-text-blue-700 tw-border-blue-300',
        buttonColor: 'tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white',
        buttonText: 'View Progress',
        icon: <RefreshCw className="tw-h-4 tw-w-4 tw-text-blue-500" />,
        urgency: 'low',
      };
    default:
      return {
        bgColor: 'tw-bg-gray-50 tw-border-gray-200',
        badgeColor: 'tw-bg-gray-100 tw-text-gray-700 tw-border-gray-300',
        buttonColor: 'tw-bg-gray-600 hover:tw-bg-gray-700 tw-text-white',
        buttonText: 'View Details',
        icon: <Eye className="tw-h-4 tw-w-4 tw-text-gray-500" />,
        urgency: 'low',
      };
  }
};

/**
 * Check if a request is reviewed (unified logic from request.utils.tsx)
 */
export const isRequestReviewed = (row: any) => {
  if (row.requested_reedit_at) {
    return false;
  }
  if (row.requested_attention_at) {
    return false;
  }
  if (row.status === 'ready_for_review') {
    return false;
  }
  return !!row.reviewed;
};

/**
 * Check if customer has viewed request since re-edit was requested
 */
const hasViewedRequestSinceReedit = (request: any): boolean => {
  try {
    const viewedData = localStorage.getItem('last_viewed_request');
    if (!viewedData) {
      // No localStorage data, fall back to request.reviewed
      return request.reviewed ? new Date(request.reviewed) > new Date(request.requested_reedit_at) : false;
    }
    
    const { requestId: storedRequestId, timestamp } = JSON.parse(viewedData);
    
    if (storedRequestId === request.id) {
      // Current request matches stored request, use localStorage timestamp
      return new Date(timestamp) > new Date(request.requested_reedit_at);
    } else {
      // Different request, fall back to request.reviewed
      return request.reviewed ? new Date(request.reviewed) > new Date(request.requested_reedit_at) : false;
    }
  } catch {
    // Error parsing localStorage, fall back to request.reviewed
    return request.reviewed ? new Date(request.reviewed) > new Date(request.requested_reedit_at) : false;
  }
};

/**
 * Mark request as viewed by customer
 */
export const markRequestAsViewed = (requestId: string) => {
  try {
    localStorage.setItem('last_viewed_request', JSON.stringify({
      requestId,
      timestamp: new Date().toISOString()
    }));
  } catch {
    // Handle localStorage errors silently
  }
};

/**
 * Get the table row variant for a request
 * This matches the logic used in requests-list-page.tsx
 */
export const getRequestRowVariant = (
  request: any,
): 'needs_attention' | undefined => {
  if (request.requested_attention_at) {
    return 'needs_attention';
  }
  if (request.requested_reedit_at && !hasViewedRequestSinceReedit(request)) {
    return 'needs_attention';
  }
  if (!isRequestReviewed(request) && !request.requested_reedit_at) {
    return 'needs_attention';
  }
  return undefined;
};

/**
 * Get the status details component for a request (same as used in requests list)
 */
export const getRequestStatusDetailsComponent = (request: any, size: 'sm' | 'default' = 'sm') => {
  return (
    <RequestStatusDetails
      needsAttention={request.requested_attention_at}
      needsReedit={request.requested_reedit_at}
      approved={request.approved_at}
      size={size}
    />
  );
};

export const getRequestStatusDescription = (request: RequestsTableResponse) => {
  if (request.status === requestStatus.uploading) {
    return 'Media is being uploaded';
  }
  if (request.status === requestStatus.delivered) {
    return `Overdue by ${Math.max(1, Math.floor((Date.now() - new Date(request.status_changed_at!).getTime()) / (1000 * 60 * 60 * 24)))} days`;
  }
  if (request.status === requestStatus.queued) {
    return 'Waiting to be started';
  }
  if (request.status === requestStatus.currentlyEditing) {
    return 'Currently being edited';
  }
  if (request.status === requestStatus.complete) {
    return 'Request approved';
  }
};
